import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useRouter } from 'next/compat/router'
import { useTranslation } from 'react-i18next'

import { Box, useDisclosure } from '@chakra-ui/react'
import { LayoutGroup, motion } from 'framer-motion'

import type { ColorScheme } from '@hivewatch/dark'
import {
  BadgeCounter,
  Button,
  FilterIcon,
  IconButton,
  TABLE_MAX_WIDTH,
  TableToolbar,
} from '@hivewatch/dark'

import { useAppSelector } from '@/redux/hooks'
import {
  IncidentsView,
  selectActiveFilter,
  selectIncidentFilterCount,
} from '@/redux/incidents/incidentsSlice'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'
import { DASHBOARD } from '@/utils/routes'

import {
  ActiveFilterBadge,
  DarkIncidentsOwnerToggle,
} from '../incidents-filter-drawer'
import { DarkIncidentsFilterDrawer } from '../incidents-filter-drawer/dark'
import { DarkNextUpButton } from './DarkNextUpButton'

interface IProps {
  colorScheme: ColorScheme
  filter: string | null
  setFilter: (value: string) => void
  incidentsView: IncidentsView
  onSelectView: (view: IncidentsView) => void
}

export const DarkIncidentsSearchFilterHeader = ({
  colorScheme,
  filter,
  setFilter,
  incidentsView,
  onSelectView,
}: IProps) => {
  const { t } = useTranslation()
  const { shouldShowMetricsBtn } = usePermissions()
  const { push } = useRouter()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const filterCount = useAppSelector(selectIncidentFilterCount)
  const activeFilter = useAppSelector(selectActiveFilter)
  const isPriorityQueueEnabled = useFeatureFlag(FeatureFlag.priorityQueue)
  const isPriorityQueueActive = incidentsView === 'priorityQueue'

  return (
    <>
      <Box px={6} py={2}>
        <TableToolbar
          actions={
            <Box alignItems='center' display='flex' gap='16px'>
              <LayoutGroup>
                <motion.div
                  layout
                  style={{ display: 'flex' }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <DarkIncidentsOwnerToggle
                    colorScheme={colorScheme}
                    onSelectView={onSelectView}
                    view={incidentsView}
                  />
                </motion.div>
                {isPriorityQueueEnabled && (
                  <DarkNextUpButton
                    colorScheme={colorScheme}
                    isActive={isPriorityQueueActive}
                  />
                )}
                <motion.div
                  layout
                  style={{ alignItems: 'center', display: 'flex', gap: '16px' }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  {shouldShowMetricsBtn && (
                    <Button
                      colorScheme={colorScheme}
                      onClick={() => {
                        trackEvent(
                          `${BUTTON_PRESS} Metrics - from Incidents page`
                        )
                        push(DASHBOARD)
                      }}
                      variant='secondary'
                    >
                      {t('incidents:table_metrics')}
                    </Button>
                  )}
                  {activeFilter && (
                    <ActiveFilterBadge label={activeFilter?.name} />
                  )}
                  <Box
                    alignItems='center'
                    cursor='pointer'
                    data-testid='incidentsPage_tableHeader_filterButton'
                    display='flex'
                    gap='8px'
                    onClick={onOpen}
                  >
                    {filterCount > 0 && (
                      <BadgeCounter
                        colorScheme={colorScheme}
                        count={filterCount}
                        variant='button'
                      />
                    )}
                    <IconButton
                      aria-label={t('common:filters')}
                      colorScheme={colorScheme}
                      dimension={36}
                      icon={<FilterIcon colorScheme={colorScheme} />}
                    />
                  </Box>
                </motion.div>
              </LayoutGroup>
            </Box>
          }
          colorScheme={colorScheme}
          maxWidth={TABLE_MAX_WIDTH}
          onChange={setFilter}
          placeholder={t('common:search_or_filter')}
          value={filter ?? ''}
        />
      </Box>
      <DarkIncidentsFilterDrawer isOpen={isOpen} onClose={onClose} />
    </>
  )
}
